import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { ProjectWithCounts, PaginatedResponse, CreateProject, UpdateProject } from '@estimator/shared';

export function useProjects(params?: {
  page?: number;
  status?: string;
  type?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: ['projects', params],
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<ProjectWithCounts>>('/projects', {
        params,
      });
      return data;
    },
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const { data } = await apiClient.get<ProjectWithCounts>(`/projects/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProject) => {
      const { data: result } = await apiClient.post('/projects', data);
      return result;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateProject & { id: string }) => {
      const { data } = await apiClient.patch<ProjectWithCounts>(`/projects/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['project', vars.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['calculations'] });
    },
  });
}

export function useIntakeProject() {
  return useMutation({
    mutationFn: async ({ projectId, files }: { projectId: string; files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      const { data } = await apiClient.post(`/projects/${projectId}/intake`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}
